import { isSupportedConvertImageFile } from '../convert/convertImageEngine';
import { encodeCanvas, legacyMetaFor, resolveOutputFormat } from '../shared/imageOutputFormats';

export { formatFileSize } from '../compress/compressImageEngine';

export const isSupportedWatermarkImageFile = isSupportedConvertImageFile;

export const POSITION_OPTIONS = [
    'center',
    'top-left',
    'top-right',
    'bottom-left',
    'bottom-right',
];

const PAD = 20;

export function getWatermarkCenterPixels(canvasW, canvasH, wmW, wmH, position, customCenterX, customCenterY) {
    if (position === 'custom' && customCenterX != null && customCenterY != null) {
        return { cx: customCenterX, cy: customCenterY };
    }
    switch (position) {
        case 'top-left':
            return { cx: PAD + wmW / 2, cy: PAD + wmH / 2 };
        case 'top-right':
            return { cx: canvasW - PAD - wmW / 2, cy: PAD + wmH / 2 };
        case 'bottom-left':
            return { cx: PAD + wmW / 2, cy: canvasH - PAD - wmH / 2 };
        case 'center':
            return { cx: canvasW / 2, cy: canvasH / 2 };
        case 'bottom-right':
        default:
            return { cx: canvasW - PAD - wmW / 2, cy: canvasH - PAD - wmH / 2 };
    }
}

function hexToRgba(hex, opacityPercent) {
    const raw = String(hex || '#ffffff').replace('#', '');
    const full = raw.length === 3
        ? raw.split('').map((c) => c + c).join('')
        : raw.padEnd(6, '0').slice(0, 6);
    const r = parseInt(full.slice(0, 2), 16) || 0;
    const g = parseInt(full.slice(2, 4), 16) || 0;
    const b = parseInt(full.slice(4, 6), 16) || 0;
    const a = Math.min(1, Math.max(0, (Number(opacityPercent) || 50) / 100));
    return `rgba(${r}, ${g}, ${b}, ${a})`;
}

async function prepareFile(file) {
    const name = file.name.toLowerCase();
    const isHeic = /\.(heic|heif)$/.test(name) || file.type === 'image/heic' || file.type === 'image/heif';
    if (isHeic) {
        const heic2any = (await import('heic2any')).default;
        const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
        const blob = Array.isArray(converted) ? converted[0] : converted;
        const base = file.name.replace(/\.(heic|heif)$/i, '');
        return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
    }
    return file;
}

async function loadImageElement(fileOrUrl) {
    if (typeof fileOrUrl === 'string') {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = fileOrUrl;
        });
    }

    const prepared = await prepareFile(fileOrUrl);
    const url = URL.createObjectURL(prepared);
    try {
        return await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = url;
        });
    } finally {
        URL.revokeObjectURL(url);
    }
}

function outputMeta(file) {
    const type = (file.type || '').toLowerCase();
    const name = (file.name || '').toLowerCase();
    if (type === 'image/png' || name.endsWith('.png')) {
        return { mime: 'image/png', ext: 'png', quality: null, alpha: true };
    }
    if (type === 'image/webp' || name.endsWith('.webp')) {
        return { mime: 'image/webp', ext: 'webp', quality: 0.92, alpha: true };
    }
    return { mime: 'image/jpeg', ext: 'jpg', quality: 0.92, alpha: false };
}

function outputName(file, ext) {
    const base = file.name.replace(/\.[^.]+$/, '');
    return `${base}-watermarked.${ext}`;
}

function canvasToBlob(canvas, mime, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('encode failed'))), mime, quality ?? undefined);
    });
}

function drawTextWatermark(ctx, text, options) {
    const {
        fontSize = 96,
        color,
        position = 'center',
        customCenterX = null,
        customCenterY = null,
        rotation = 0,
        outlineColor = null,
        outlineWidth = 2,
        repeat = false,
    } = options;

    const fontSpec = `${fontSize}px "Kalpurush", "SolaimanLipi", "Noto Sans Bengali", Arial, sans-serif`;
    ctx.font = fontSpec;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';

    const metrics = ctx.measureText(text);
    const textWidth = metrics.width;
    const textHeight = fontSize;

    const drawAt = (tx, ty) => {
        if (rotation !== 0) {
            ctx.save();
            ctx.translate(tx + textWidth / 2, ty - textHeight / 2);
            ctx.rotate((rotation * Math.PI) / 180);
            if (outlineColor) {
                ctx.strokeStyle = outlineColor;
                ctx.lineWidth = outlineWidth;
                ctx.lineJoin = 'round';
                ctx.strokeText(text, -textWidth / 2, textHeight / 2);
            }
            ctx.fillText(text, -textWidth / 2, textHeight / 2);
            ctx.restore();
        } else {
            if (outlineColor) {
                ctx.strokeStyle = outlineColor;
                ctx.lineWidth = outlineWidth;
                ctx.lineJoin = 'round';
                ctx.strokeText(text, tx, ty);
            }
            ctx.fillText(text, tx, ty);
        }
    };

    ctx.fillStyle = color;

    if (repeat) {
        const gapX = textWidth * 0.6;
        const gapY = textHeight * 2.5;
        let row = 0;
        for (let ty = textHeight; ty < ctx.canvas.height + textHeight; ty += gapY) {
            const offsetX = (row % 2) * (textWidth + gapX) * 0.5;
            for (let tx = -textWidth + offsetX; tx < ctx.canvas.width + textWidth; tx += textWidth + gapX) {
                drawAt(tx, ty);
            }
            row += 1;
        }
        return;
    }

    const { cx, cy } = getWatermarkCenterPixels(
        ctx.canvas.width,
        ctx.canvas.height,
        textWidth,
        textHeight,
        position,
        customCenterX,
        customCenterY,
    );
    drawAt(cx - textWidth / 2, cy + textHeight / 2);
}

function drawLogoWatermark(ctx, logoImg, options) {
    const {
        logoSizePercent = 20,
        logoOpacity = 50,
        position = 'center',
        customCenterX = null,
        customCenterY = null,
        rotation = 0,
        repeat = false,
    } = options;

    const logoW = Math.round(ctx.canvas.width * (logoSizePercent / 100));
    const logoH = Math.round(logoW * (logoImg.naturalHeight / logoImg.naturalWidth || logoImg.height / logoImg.width));
    const rad = (rotation * Math.PI) / 180;

    ctx.save();
    ctx.globalAlpha = Math.min(1, Math.max(0.05, logoOpacity / 100));

    if (repeat) {
        const gapX = logoW * 0.8;
        const gapY = logoH * 2;
        let row = 0;
        for (let ty = 0; ty < ctx.canvas.height + logoH; ty += logoH + gapY) {
            const offsetX = (row % 2) * (logoW + gapX) * 0.5;
            for (let tx = -logoW + offsetX; tx < ctx.canvas.width + logoW; tx += logoW + gapX) {
                if (rotation !== 0) {
                    ctx.save();
                    ctx.translate(tx + logoW / 2, ty + logoH / 2);
                    ctx.rotate(rad);
                    ctx.drawImage(logoImg, -logoW / 2, -logoH / 2, logoW, logoH);
                    ctx.restore();
                } else {
                    ctx.drawImage(logoImg, tx, ty, logoW, logoH);
                }
            }
            row += 1;
        }
    } else {
        const { cx, cy } = getWatermarkCenterPixels(
            ctx.canvas.width,
            ctx.canvas.height,
            logoW,
            logoH,
            position,
            customCenterX,
            customCenterY,
        );
        const x = cx - logoW / 2;
        const y = cy - logoH / 2;
        if (rotation !== 0) {
            ctx.translate(x + logoW / 2, y + logoH / 2);
            ctx.rotate(rad);
            ctx.drawImage(logoImg, -logoW / 2, -logoH / 2, logoW, logoH);
        } else {
            ctx.drawImage(logoImg, x, y, logoW, logoH);
        }
    }

    ctx.restore();
}

/**
 * Apply a watermark to an image in the browser (matches site addWatermark behavior).
 */
export async function watermarkImageFile(file, options = {}) {
    const img = await loadImageElement(file);
    const canvasW = img.naturalWidth;
    const canvasH = img.naturalHeight;
    // The picked output format; "original" keeps the file's own (see shared/imageOutputFormats).
    const meta = legacyMetaFor(resolveOutputFormat(options.format, file));

    const canvas = document.createElement('canvas');
    canvas.width = canvasW;
    canvas.height = canvasH;
    const ctx = canvas.getContext('2d', { alpha: meta.alpha });

    if (!meta.alpha) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvasW, canvasH);
    }
    ctx.drawImage(img, 0, 0);

    const mode = options.mode === 'image' || options.watermarkType === 'logo' ? 'image' : 'text';
    const opacity = Number(options.opacity) || 50;
    const rotation = Number(options.rotation) || 0;
    const position = options.position || 'center';
    const repeat = Boolean(options.repeat || options.repeatWatermark);
    const fontSize = Math.max(14, Math.min(300, Number(options.fontSize) || 96));
    const textColor = options.textColor || '#ffffff';
    const outlineEnabled = Boolean(options.outlineEnabled);
    const outlineColor = outlineEnabled
        ? hexToRgba(options.outlineColor || '#000000', opacity)
        : null;
    const outlineWidth = Math.max(1, Math.min(10, Number(options.outlineWidth) || 2));
    const logoSizePercent = Math.max(5, Math.min(60, Number(options.imageScale ?? options.logoSizePercent) || 20));

    if (mode === 'text') {
        const text = String(options.text || options.watermarkText || '').trim();
        if (!text) throw new Error('emptyText');

        if (document?.fonts?.load) {
            try {
                await document.fonts.load(`${fontSize}px "Kalpurush"`, text);
            } catch {
                // Best effort.
            }
        }

        drawTextWatermark(ctx, text, {
            fontSize,
            color: hexToRgba(textColor, opacity),
            position,
            customCenterX: options.customCenterX ?? null,
            customCenterY: options.customCenterY ?? null,
            rotation,
            outlineColor,
            outlineWidth,
            repeat,
        });
    } else {
        const logoSource = options.imageFile || options.logoUrl;
        if (!logoSource) throw new Error('noImage');
        const logoImg = await loadImageElement(logoSource);
        drawLogoWatermark(ctx, logoImg, {
            logoSizePercent,
            logoOpacity: opacity,
            position,
            customCenterX: options.customCenterX ?? null,
            customCenterY: options.customCenterY ?? null,
            rotation,
            repeat,
        });
    }

    // Through the shared encoder: AVIF needs WebAssembly (Chrome's canvas cannot
    // write it), and the extension follows what was actually written.
    const { blob, ext } = await encodeCanvas(canvas, resolveOutputFormat(options.format, file), {
        quality: meta.quality ?? 0.92,
    });
    return {
        name: outputName(file, ext),
        blob,
        originalSize: file.size,
        watermarkedSize: blob.size,
    };
}
